<template>
  <div class="container mx-auto px-4 py-8">
    <div class="max-w-4xl mx-auto">
      <!-- 헤더 -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">질문하기</h1>
        <p class="mt-2 text-gray-600">궁금한 점을 질문해보세요</p>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- 제목 -->
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="mb-6">
            <label for="title" class="form-label">제목 *</label>
            <input
              id="title"
              v-model="form.title"
              type="text"
              required
              class="form-input"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.title }"
              placeholder="질문 제목을 입력하세요"
            />
            <p v-if="errors.title" class="form-error">{{ errors.title }}</p>
            <p class="form-hint">구체적이고 명확한 제목을 작성해주세요.</p>
          </div>
        </div>

        <!-- 내용 -->
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="mb-6">
            <label for="content" class="form-label">질문 내용 *</label>
            <textarea
              id="content"
              v-model="form.content"
              required
              rows="12"
              class="form-input resize-none"
              :class="{ 'border-red-300 focus:border-red-500 focus:ring-red-500': errors.content }"
              placeholder="질문하고 싶은 내용을 자세히 작성해주세요&#10;&#10;• 문제 상황을 구체적으로 설명해주세요&#10;• 어떤 도움이 필요한지 명확히 해주세요&#10;• 관련 정보나 스크린샷을 포함하면 더 정확한 답변을 받을 수 있습니다"
            />
            <p v-if="errors.content" class="form-error">{{ errors.content }}</p>
            <p class="form-hint">HTML 태그를 사용하여 서식을 지정할 수 있습니다.</p>
          </div>
        </div>

        <!-- 미리보기 (내용이 있을 때만) -->
        <div v-if="form.content.trim()" class="bg-white rounded-lg shadow-sm border p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">미리보기</h3>
          <div class="prose prose-lg max-w-none border-t pt-4" v-html="form.content"></div>
        </div>

        <!-- 질문 가이드라인 -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 class="text-lg font-semibold text-blue-900 mb-3">질문하기 전에 확인해주세요</h3>
          <div class="space-y-2 text-sm text-blue-800">
            <div class="flex items-start">
              <CheckCircleIcon class="w-5 h-5 mr-2 mt-0.5 text-blue-600" />
              <span>비슷한 질문이 이미 있는지 검색해보셨나요?</span>
            </div>
            <div class="flex items-start">
              <CheckCircleIcon class="w-5 h-5 mr-2 mt-0.5 text-blue-600" />
              <span>문제 상황을 구체적으로 설명하셨나요?</span>
            </div>
            <div class="flex items-start">
              <CheckCircleIcon class="w-5 h-5 mr-2 mt-0.5 text-blue-600" />
              <span>어떤 도움이 필요한지 명확하게 표현하셨나요?</span>
            </div>
          </div>
        </div>

        <!-- 액션 버튼 -->
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="flex justify-between">
            <RouterLink to="/qna" class="btn btn-outline btn-sm">
              <XMarkIcon class="w-5 h-5 mr-2" />
              취소
            </RouterLink>
            
            <div class="flex gap-3">
              <button
                type="button"
                @click="resetForm"
                class="btn btn-outline btn-sm"
              >
                <ArrowPathIcon class="w-5 h-5 mr-2" />
                초기화
              </button>
              <button
                type="submit"
                :disabled="isSubmitting || !isFormValid"
                class="btn btn-primary btn-sm"
                :class="{ 'opacity-50 cursor-not-allowed': isSubmitting || !isFormValid }"
              >
                <span v-if="isSubmitting" class="flex items-center">
                  <div class="spinner w-5 h-5 mr-2"></div>
                  질문 등록 중...
                </span>
                <span v-else class="flex items-center">
                  <QuestionMarkCircleIcon class="w-5 h-5 mr-2" />
                  질문 등록하기
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- 일반 에러 메시지 -->
        <div v-if="errors.general" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <p class="text-red-600">{{ errors.general }}</p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import {
  XMarkIcon,
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import { qnaApi } from '@/services/api'
import type { QnaRequest } from '@/types'
import { useToast } from 'vue-toastification'

const router = useRouter()
const toast = useToast()

const isSubmitting = ref(false)

// 폼 데이터
const form = reactive<QnaRequest>({
  title: '',
  content: ''
})

// 에러 상태
const errors = reactive({
  title: '',
  content: '',
  general: ''
})

// 폼 유효성 검사
const isFormValid = computed(() => {
  return form.title.trim() && form.content.trim()
})

// 에러 초기화
const clearErrors = () => {
  errors.title = ''
  errors.content = ''
  errors.general = ''
}

// 폼 유효성 검사
const validateForm = (): boolean => {
  clearErrors()
  let isValid = true

  if (!form.title.trim()) {
    errors.title = '제목을 입력해주세요.'
    isValid = false
  } else if (form.title.length > 200) {
    errors.title = '제목은 200자 이하로 입력해주세요.'
    isValid = false
  }

  if (!form.content.trim()) {
    errors.content = '질문 내용을 입력해주세요.'
    isValid = false
  } else if (form.content.length > 10000) {
    errors.content = '내용은 10,000자 이하로 입력해주세요.'
    isValid = false
  }

  return isValid
}

// 폼 초기화
const resetForm = () => {
  form.title = ''
  form.content = ''
  clearErrors()
}

// 폼 제출
const handleSubmit = async () => {
  if (!validateForm()) return

  try {
    isSubmitting.value = true
    const response = await qnaApi.createQna(form)
    toast.success('질문이 성공적으로 등록되었습니다.')
    router.push(`/qna/${response.data.id}`)
  } catch (error: any) {
    console.error('Failed to create qna:', error)
    if (error.response?.status === 400) {
      errors.general = '입력 데이터를 확인해주세요.'
    } else if (error.response?.status === 401) {
      errors.general = '로그인이 필요합니다.'
    } else {
      errors.general = '질문 등록에 실패했습니다. 다시 시도해주세요.'
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h1, .prose h2, .prose h3, .prose h4 {
  color: #111827;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.prose p {
  margin-bottom: 1rem;
}

.prose ul, .prose ol {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.prose blockquote {
  border-left: 3px solid #e5e7eb;
  padding-left: 1rem;
  font-style: italic;
  color: #6b7280;
  margin: 1rem 0;
}

.prose strong {
  font-weight: 600;
  color: #111827;
}
</style>